import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, ChevronRight, CheckCircle, Clock, MessageSquare, LogOut, Sparkles, BarChart, Target, Users, Briefcase } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AITaskAssignment from '../components/AITaskAssignment';
import AIDecisionMaking from '../components/AIDecisionMaking';
import AIFeedbackSystem from '../components/AIFeedbackSystem';

const EntrepreneurTrainingPage = () => {
  const [activeTab, setActiveTab] = useState('tareas');
  const [showChatbot, setShowChatbot] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  
  const userRole = user?.user_metadata?.role || 'entrepreneur';

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Estadísticas de progreso del emprendedor
  const entrepreneurStats = {
    progress: 45,
    completedTasks: 3,
    totalTasks: 12,
    averageScore: 7.8,
    timeSpent: '18h 45m',
    nextMilestone: 'Validación de producto'
  };

  // Etapas del proceso emprendedor
  const entrepreneurStages = [
    {
      id: 'ideation',
      title: 'Ideación',
      description: 'Generar y validar ideas de negocio',
      progress: 80,
      status: 'in-progress'
    },
    {
      id: 'validation',
      title: 'Validación',
      description: 'Validar el problema y la solución',
      progress: 30,
      status: 'in-progress'
    },
    {
      id: 'mvp',
      title: 'MVP',
      description: 'Desarrollar un producto mínimo viable',
      progress: 10,
      status: 'in-progress'
    },
    {
      id: 'launch',
      title: 'Lanzamiento',
      description: 'Lanzar el producto al mercado',
      progress: 0,
      status: 'locked'
    },
    {
      id: 'growth',
      title: 'Crecimiento',
      description: 'Escalar el negocio',
      progress: 0,
      status: 'locked'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Dashboard Header/Navbar */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Sparkles className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-indigo-600">RealMentor AI</span>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">
                  Hola, {user?.user_metadata?.name || 'Emprendedor'}
                </span>
                <button
                  onClick={handleSignOut}
                  className="flex items-center text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                >
                  <LogOut className="h-4 w-4 mr-1" />
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Entrenamiento Empresarial
            </h2>
            <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <Rocket className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                Ruta: Emprendimiento
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <BarChart className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                Progreso: {entrepreneurStats.progress}%
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <Clock className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                Tiempo dedicado: {entrepreneurStats.timeSpent}
              </div>
            </div>
          </div>
          <div className="mt-5 flex lg:mt-0 lg:ml-4">
            <span className="hidden sm:block ml-3">
              <button
                type="button"
                onClick={() => setShowChatbot(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                <MessageSquare className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Hablar con mi mentor
              </button>
            </span>
          </div>
        </div>

        {/* Progreso del emprendimiento */}
        <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <Rocket className="h-5 w-5 mr-2 text-accent" />
              Tu viaje emprendedor
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Sigue tu progreso a través de las etapas clave del emprendimiento
            </p>
          </div>
          <div className="border-t border-gray-200">
            <div className="px-4 py-5 sm:p-6">
              <div className="space-y-6">
                {entrepreneurStages.map((stage) => (
                  <div key={stage.id} className="relative">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                        stage.status === 'locked' 
                          ? 'bg-gray-200 text-gray-400' 
                          : stage.progress === 100 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-accent/10 text-accent'
                      }`}>
                        {stage.progress === 100 ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : stage.status === 'locked' ? (
                          <span>{entrepreneurStages.findIndex(s => s.id === stage.id) + 1}</span>
                        ) : (
                          <span>{entrepreneurStages.findIndex(s => s.id === stage.id) + 1}</span>
                        )}
                      </div>
                      <div className="ml-4 flex-1">
                        <h4 className="text-sm font-medium text-gray-900 flex items-center">
                          {stage.title}
                          {stage.status === 'locked' && (
                            <Clock className="ml-2 h-4 w-4 text-gray-400" />
                          )}
                          {stage.progress === 100 && (
                            <CheckCircle className="ml-2 h-4 w-4 text-green-500" />
                          )}
                        </h4>
                        <p className="text-sm text-gray-500">{stage.description}</p>
                        {stage.status !== 'locked' && (
                          <div className="mt-2">
                            <div className="flex items-center">
                              <div className="flex-1">
                                <div className="h-2 bg-gray-200 rounded-full">
                                  <div
                                    className={`h-2 rounded-full ${
                                      stage.progress === 100 
                                        ? 'bg-green-500' 
                                        : 'bg-accent'
                                    }`}
                                    style={{ width: `${stage.progress}%` }}
                                  ></div>
                                </div>
                              </div>
                              <span className="ml-2 text-xs text-gray-500">{stage.progress}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                      {stage.status !== 'locked' && (
                        <div className="ml-4">
                          <button className="text-accent hover:text-accent/90 text-sm flex items-center">
                            {stage.progress === 100 ? 'Revisar' : 'Continuar'}
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    {entrepreneurStages.findIndex(s => s.id === stage.id) < entrepreneurStages.length - 1 && (
                      <div className="absolute left-4 top-8 h-full w-px bg-gray-200"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('tareas')}
                    className={`${
                      activeTab === 'tareas'
                        ? 'border-accent text-accent'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
                  >
                    Tareas
                  </button>
                  <button
                    onClick={() => setActiveTab('decisiones')}
                    className={`${
                      activeTab === 'decisiones'
                        ? 'border-accent text-accent'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
                  >
                    Simulador de Decisiones
                  </button>
                  <button
                    onClick={() => setActiveTab('recursos')}
                    className={`${
                      activeTab === 'recursos'
                        ? 'border-accent text-accent'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } w-1/3 py-4 px-1 text-center border-b-2 font-medium text-sm`}
                  >
                    Recursos
                  </button>
                </nav>
              </div>

              <div className="p-0">
                {activeTab === 'tareas' && (
                  <AITaskAssignment 
                    careerPath={userRole} 
                    onChatWithMentor={() => setShowChatbot(true)} 
                  />
                )}

                {activeTab === 'decisiones' && (
                  <AIDecisionMaking 
                    careerPath={userRole} 
                    onChatWithMentor={() => setShowChatbot(true)} 
                  />
                )}

                {activeTab === 'recursos' && (
                  <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Recursos para emprendedores</h3>
                    <div className="space-y-6">
                      <div className="border rounded-lg p-4 hover:border-accent hover:shadow-sm transition-all">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Target className="h-6 w-6 text-accent" />
                          </div>
                          <div className="ml-4">
                            <h4 className="text-md font-medium text-gray-900">Validación de ideas de negocio</h4>
                            <p className="mt-1 text-sm text-gray-500">
                              Aprende a validar tus ideas de negocio con métodos ágiles y económicos.
                            </p>
                            <div className="mt-3">
                              <a href="#" className="text-accent hover:text-accent/90 text-sm flex items-center">
                                Ver guía completa
                                <ChevronRight className="ml-1 h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border rounded-lg p-4 hover:border-accent hover:shadow-sm transition-all">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Users className="h-6 w-6 text-accent" />
                          </div>
                          <div className="ml-4">
                            <h4 className="text-md font-medium text-gray-900">Adquisición de primeros clientes</h4>
                            <p className="mt-1 text-sm text-gray-500">
                              Estrategias efectivas para conseguir tus primeros clientes sin un gran presupuesto de marketing.
                            </p>
                            <div className="mt-3">
                              <a href="#" className="text-accent hover:text-accent/90 text-sm flex items-center">
                                Ver estrategias
                                <ChevronRight className="ml-1 h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="border rounded-lg p-4 hover:border-accent hover:shadow-sm transition-all">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <Briefcase className="h-6 w-6 text-accent" />
                          </div>
                          <div className="ml-4">
                            <h4 className="text-md font-medium text-gray-900">Financiamiento para startups</h4>
                            <p className="mt-1 text-sm text-gray-500">
                              Guía completa sobre opciones de financiamiento, desde bootstrapping hasta capital de riesgo.
                            </p>
                            <div className="mt-3">
                              <a href="#" className="text-accent hover:text-accent/90 text-sm flex items-center">
                                Explorar opciones
                                <ChevronRight className="ml-1 h-4 w-4" />
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Estadísticas */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tus estadísticas</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-500">Tareas completadas</div>
                    <div className="mt-1 flex items-center">
                      <span className="text-2xl font-bold text-gray-900">{entrepreneurStats.completedTasks}</span>
                      <span className="ml-1 text-sm text-gray-500">/ {entrepreneurStats.totalTasks}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-500">Puntuación media</div>
                    <div className="mt-1 flex items-center">
                      <span className="text-2xl font-bold text-gray-900">{entrepreneurStats.averageScore}</span>
                      <span className="ml-1 text-sm text-gray-500">/ 10</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-500">Tiempo dedicado</div>
                    <div className="mt-1">
                      <span className="text-2xl font-bold text-gray-900">{entrepreneurStats.timeSpent}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-500">Próximo hito</div>
                    <div className="mt-1">
                      <span className="text-sm font-medium text-gray-900">{entrepreneurStats.nextMilestone}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Próximos eventos */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-5">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Próximos eventos</h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent pl-4">
                    <div className="text-sm font-medium text-gray-900">Taller de Validación de Ideas</div>
                    <div className="mt-1 text-xs text-gray-500">Mañana, 18:00 - 19:30</div>
                    <div className="mt-2">
                      <button className="text-accent hover:text-accent/90 text-xs font-medium">
                        Añadir a calendario
                      </button>
                    </div>
                  </div>
                  <div className="border-l-4 border-gray-200 pl-4">
                    <div className="text-sm font-medium text-gray-900">Networking con Inversores</div>
                    <div className="mt-1 text-xs text-gray-500">Viernes, 17:00 - 20:00</div>
                    <div className="mt-2">
                      <button className="text-accent hover:text-accent/90 text-xs font-medium">
                        Añadir a calendario
                      </button>
                    </div>
                  </div>
                  <div className="border-l-4 border-gray-200 pl-4">
                    <div className="text-sm font-medium text-gray-900">Masterclass: Pitch Perfecto</div>
                    <div className="mt-1 text-xs text-gray-500">Próximo Martes, 19:00 - 20:30</div>
                    <div className="mt-2">
                      <button className="text-accent hover:text-accent/90 text-xs font-medium">
                        Añadir a calendario
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mentor IA */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <MessageSquare className="h-6 w-6 text-accent" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Tu mentor IA</h3>
                    <p className="text-sm text-gray-500">Disponible 24/7 para resolver tus dudas</p>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    onClick={() => setShowChatbot(true)}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                  >
                    Iniciar conversación
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <AIFeedbackSystem 
              careerPath={userRole} 
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EntrepreneurTrainingPage;